import { useState, useRef, useEffect } from 'react';

import PropTypes from 'prop-types';
import { BsChevronDown } from 'react-icons/bs';

import TextInput from '#globalComponents/Forms/TextInput';

import CATEGORIES from './categories';

export default function CategoryDropdown({ value, onChange, className = '' }) {
	const [isOpen, setIsOpen] = useState(false);
	const [searchTerm, setSearchTerm] = useState('');
	const dropdownRef = useRef(null);

	// Value is a single-select (array with max 1 item)
	const selectedValues = Array.isArray(value) ? value : value ? [value] : [];
	const selectedValue = selectedValues[0] || null;

	// Close dropdown when clicking outside
	useEffect(() => {
		function handleClickOutside(event) {
			if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
				setIsOpen(false);
			}
		}

		document.addEventListener('mousedown', handleClickOutside);
		return () => document.removeEventListener('mousedown', handleClickOutside);
	}, []);

	// Filter categories based on search term
	const filteredCategories = CATEGORIES.filter((category) =>
		category.label.toLowerCase().includes(searchTerm.toLowerCase()),
	);

	// Get selected category label
	const selectedLabel = selectedValue
		? CATEGORIES.find((cat) => cat.value === selectedValue)?.label
		: 'Beispielkategorie wählen';

	const handleSelect = (categoryValue) => {
		// Single select: if clicking the same category, deselect it; otherwise select the new one
		if (selectedValue === categoryValue) {
			onChange([]);
		} else {
			onChange([categoryValue]);
		}
		setIsOpen(false); // Close dropdown after selection
	};

	return (
		<div ref={dropdownRef} className={`relative ${className}`}>
			{/* Dropdown Button */}
			<button
				type="button"
				onClick={() => setIsOpen(!isOpen)}
				className="flex items-center justify-between w-full h-12 px-4 bg-white border border-gray-300 rounded-lg shadow-sm hover:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500 transition-all"
			>
				<span className="text-md font-medium text-text">{selectedLabel}</span>
				<BsChevronDown
					className={`w-4 h-4 text-bgBase-800 transition-transform ${isOpen ? 'rotate-180' : ''}`}
				/>
			</button>

			{/* Dropdown Menu */}
			{isOpen && (
				<div className="absolute z-50 w-full mt-2 bg-white border border-bgBase-500 rounded-card shadow-elevated max-h-96 overflow-hidden">
					{/* Search Input */}
					<div className="p-3 border-b border-gray-200">
						<TextInput
							placeholder="Kategorie suchen..."
							value={searchTerm}
							onChange={(e) => setSearchTerm(e.target.value)}
							autoFocus
						/>
					</div>

					{/* Category List */}
					<div className="overflow-y-auto max-h-80">
						{filteredCategories.length > 0 ? (
							<ul className="py-1">
								{filteredCategories.map((category) => (
									<li key={category.value}>
										<button
											type="button"
											onClick={() => handleSelect(category.value)}
											className={`w-full text-left px-4 py-2 text-sm hover:bg-primary-100 transition-colors ${
												selectedValue === category.value
													? 'bg-primary-100 text-primary-700 font-semibold'
													: 'text-text'
											}`}
										>
											{category.label}
										</button>
									</li>
								))}
							</ul>
						) : (
							<div className="px-4 py-8 text-center text-sm text-bgBase-800">
								Keine Kategorien gefunden
							</div>
						)}
					</div>
				</div>
			)}
		</div>
	);
}

CategoryDropdown.propTypes = {
	value: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
	onChange: PropTypes.func.isRequired,
	className: PropTypes.string,
};
